import React, { useMemo } from 'react';
import { Popover } from 'antd';
import { EyeInvisibleOutlined } from '@ant-design/icons';
import { connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router';
import { getEntityById, nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { getInternalNotesChannelId } from '../../utils/WorkOrderUtils';
import { ContactAvatar } from '../contact_avatar/ContactAvatar';
import { fetchInternalWorkspaceForSupplier } from '../../thunks/messages_thunks';
import useMountEffect from '../../hooks/useMountEffect';

import './InternalWorkspaceNotVisibleIndicator.less';

interface Participant {
	email: string;
	nameGiven?: string;
	nameFamily?: string;
	photoLink?: string;
}

// 'Not visible to' = every WO subscriber who is NOT on the current user's own
// supplier facility (internal team). Internal notes are private to that facility,
// so anyone else — buyers, third-party suppliers, subcontractors — cannot see them.
//
// A contact can see the notes only when they are a supplier on the current
// facility. We gather subscribers from two independent parts:
//   - Downstream: if subContractedToWorkOrder is present, the WO was subcontracted
//     out, so that sub leg's contractor + sub subscribers are ALWAYS included —
//     they're the facilities we passed the WO down to and are never on our own
//     team. This holds regardless of which leg we're currently viewing.
//   - Client side: if subContractedFromWorkOrder is present, this WO is itself a
//     subcontracted leg, so the real client-side subscribers live on that
//     originating leg (its contractor + sub subscribers). Otherwise this is the
//     root WO, so its own buyer + supplier subscribers are the client side.
//
// NOTE: suppliers without a facilityId (e.g. a freshly invited subcontractor
// email) are treated as a different facility and included.
export const getNotVisibleToPeople = (workOrder: any, currentFacilityId: any): Participant[] => {
	const result: Participant[] = [];
	const seen: { [email: string]: boolean } = {};

	const add = (contact: any) => {
		if (!contact || !nullSafeGet('email', contact)) return;
		const email = (contact.email as string).trim().toLowerCase();
		if (seen[email]) return;
		// Skip the current facility's own team — they can see the internal notes.
		const isOwnTeam =
			nullSafeGet('contactType', contact) === 'supplier' &&
			nullSafeGet('facilityId', contact) === currentFacilityId;
		if (isOwnTeam) return;
		seen[email] = true;
		result.push(contact);
	};

	const addAll = (path: string, entity: any) =>
		nullSafeGetOrElse(path, entity, []).forEach(add);

	const subContractedFrom = nullSafeGet('subContractedFromWorkOrder', workOrder);
	const subContractedTo = nullSafeGet('subContractedToWorkOrder', workOrder);

	// Downstream — the facilities we passed this WO down to. Always included when
	// present; they're never on our own facility. On the nested sub-WO the
	// buyer/supplier roles are renamed to contractor/sub, so those are the two
	// subscriber arrays it carries (see getSubcontractedValues in WorkOrderUtils).
	if (subContractedTo) {
		addAll('contractorSubscribers', subContractedTo);
		addAll('subSubscribers', subContractedTo);
	}

	// Client side — depends on whether this WO is itself a subcontracted leg.
	if (subContractedFrom) {
		// Subcontracted leg — the real client-side subscribers live on the
		// originating (root) WO rather than this leg's own buyer/supplier lists.
		addAll('contractorSubscribers', subContractedFrom);
		addAll('subSubscribers', subContractedFrom);
	} else {
		// Root WO — its own buyer/supplier subscribers are the client side.
		addAll('buyerSubscribers', workOrder);
		addAll('supplierSubscribers', workOrder);
	}

	return result;
};

const fullName = (person: Participant): string => {
	const name = `${nullSafeGetOrElse('nameGiven', person, '')} ${nullSafeGetOrElse(
		'nameFamily',
		person,
		''
	)}`.trim();
	return name || nullSafeGetOrElse('email', person, '');
};

interface InternalWorkspaceNotVisibleIndicatorProps extends RouteComponentProps<{ id: string }> {
	workOrder: any;
	workOrderId: string;
	currentFacilityId: any;
	channelHydrated: boolean;
	fetchInternalWorkspace: (workOrderId: string) => Promise<any>;
}

const InternalWorkspaceNotVisibleIndicator = ({
	workOrder,
	workOrderId,
	currentFacilityId,
	channelHydrated,
	fetchInternalWorkspace,
}: InternalWorkspaceNotVisibleIndicatorProps) => {
	useMountEffect(() => {
		// Hydrate the channel so the notes tab has its data when opened.
		if (workOrderId && !channelHydrated) {
			fetchInternalWorkspace(workOrderId);
		}
	});

	const notVisibleTo = useMemo(
		() => getNotVisibleToPeople(workOrder, currentFacilityId),
		[workOrder, currentFacilityId]
	);

	if (notVisibleTo.length === 0) {
		return null;
	}

	const firstPerson = notVisibleTo[0];
	const remaining = notVisibleTo.length - 1;

	const popoverContent = (
		<div className="flex max-h-80 w-[295px] flex-col overflow-y-auto">
			<div className="bodyregular border-0 border-b border-solid border-[#D9D9D9] py-3 pl-4 pr-2.5 text-[rgba(38,38,38,0.8)]">
				Internal notes and attachments are <span className="bodybold">not visible</span> to the
				following WO subscribers:
			</div>
			{notVisibleTo.map((person) => (
				<div key={nullSafeGet('email', person)} className="flex items-center gap-2 px-4 py-3">
					<ContactAvatar hidePopover userType="buyer" size={32} contact={person} />
					<div className="flex min-w-0 flex-col">
						<span className="bodyregular text-[rgba(0,0,0,0.7)]">{fullName(person)}</span>
						<span className="footnoteMedium truncate text-character-secondary">
							{nullSafeGet('email', person)}
						</span>
					</div>
				</div>
			))}
		</div>
	);

	return (
		<span className="mt-1 inline-flex cursor-pointer items-center gap-1 text-character-secondary">
			<EyeInvisibleOutlined />
			<span className="bodyregular text-character-secondary">
				<span className="bodybold">NOT</span> visible to
			</span>
			<ContactAvatar
				hidePopover
				userType="buyer"
				size={24}
				style={{ fontSize: 12 }}
				contact={firstPerson}
			/>
			<span className="bodyregular max-w-[180px] truncate text-character-secondary">
				{fullName(firstPerson)}
			</span>
			{remaining > 0 ? (
				<Popover
					content={popoverContent}
					placement="bottom"
					overlayClassName="internalWorkspaceNotVisibleIndicator_popover"
				>
					<span className="bodyregularunderline text-character-secondary">
						+{remaining} other{remaining > 1 ? 's' : ''}
					</span>
				</Popover>
			) : null}
		</span>
	);
};

const mapStateToProps = (state: any, ownProps: any) => {
	const workOrderId = nullSafeGet('match.params.id', ownProps);
	const channelId = workOrderId ? getInternalNotesChannelId(workOrderId) : null;
	const channelState = channelId ? state.messages[channelId] : null;
	return {
		workOrder: getEntityById(workOrderId, state.work_orders),
		workOrderId,
		currentFacilityId: nullSafeGet('session.currentUser.facility.id', state),
		channelHydrated: !!(channelState && channelState.isLoading === false),
	};
};

const mapDispatchToProps = (dispatch: any) => ({
	fetchInternalWorkspace: (workOrderId: string) =>
		dispatch(fetchInternalWorkspaceForSupplier(workOrderId)),
});

export default withRouter(
	connect(mapStateToProps, mapDispatchToProps)(InternalWorkspaceNotVisibleIndicator)
);
